<template>
<div>
    <div v-if="teamMetrics">
        <div class="d-flex align-items-center justify-content-between mb-5">
            <h5 class="header-case">
                {{ team.name }}
                <div class="d-inline-block align-middle" v-if="!partner">
                    <button
                        class="btn btn-danger"
                        :disabled="busy"
                        @click="deactivate"
                        v-if="team.current_status == 'UP'"
                    >
                        DEACTIVATE
                    </button>
                    <button
                        class="btn btn-success"
                        :disabled="busy"
                        @click="reactivate"
                        v-if="
                            team.current_status == 'NO_IMPORT' &&
                                team.behemoth_watcher_id
                        "
                    >
                        RE-ACTIVATE
                    </button>
                </div>
            </h5>
            <div v-if="teamMetrics.subscribed">
                <i class="fa fa-check text-success"></i>
                <strong>Subscribed</strong>
                <span>until {{ teamMetrics.subscription_ends_at }}</span>
            </div>
            <div v-else-if="team.status == 'trial'">
                <i class="fa fa-alarm"></i>
                <strong>On Trial</strong>
                <span>until {{ trialEndsAt }}</span>
            </div>
            <div v-else>
                <span class="light-text">Expired</span>
            </div>
            <button v-if="team.is_initial_loading_complete===0" @click="enableInitialLoading" class="btn btn-success ml-2">
                    Enable Initial Loading
                </button>
        </div>
        <div>
            <div class="metrics row">
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Reviews</strong>
                    </p>
                    <p class="metric-number">
                        {{ teamMetrics.number_of_reviews }}
                    </p>
                </div>
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Sources</strong>
                        <a
                            v-if="team.behemoth_watcher_id"
                            target="_blank"
                            class="blue-link"
                            :href="`${manageSourcesUrl}/search?watcherId=` + team.behemoth_watcher_id"
                        >
                            Manage
                        </a>
                        <a
                            v-else
                            target="_blank"
                            class="blue-link"
                            style="cursor: pointer;"
                            @click="showAddWatcherIdPopup"
                        >
                            Assign ID
                        </a>
                    </p>
                    </p>
                    <p class="metric-number">
                        {{ teamMetrics.number_of_review_sources }}
                    </p>
                </div>
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Competitors</strong>
                    </p>
                    <p class="metric-number">
                        {{ teamMetrics.number_of_competitors }}/{{
                            team.max_competitors
                        }}
                    </p>
                </div>
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Invitations</strong>
                    </p>
                    <p class="metric-number">
                        {{ teamMetrics.number_of_open_invitations }}
                    </p>
                </div>
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Users</strong>
                    </p>
                    <p class="metric-number">
                        {{ teamMetrics.number_of_users }}
                    </p>
                </div>
                <div class="col-md-2 metric-wrapper border-right">
                    <p class="metric-label">
                        <strong>Responses</strong>
                    </p>
                    <p
                        class="metric-number"
                        v-if="
                            teamMetrics.response_rates &&
                                teamMetrics.response_rates.is_valid
                        "
                    >
                        {{ Math.round(teamMetrics.response_rates.before) }}%
                        <i class="fa fa-arrow-right"></i>
                        {{ Math.round(teamMetrics.response_rates.after) }}%
                    </p>
                </div>
            </div>
        </div>
        <div class="team-actions mt-4 d-flex justify-content-between">
            <div class="col px-2">
                <a
                    href="#"
                    class="tab border-bottom text-center d-block w-100 pb-3 light-text"
                    :class="{ active: tab == 'subscription' }"
                    @click.prevent="openTab('subscription')"
                    >Subscription</a
                >
            </div>
            <div class="col px-2">
                <a
                    href="#"
                    class="tab border-bottom text-center d-block w-100 pb-3 light-text"
                    :class="{ active: tab == 'settings' }"
                    @click.prevent="openTab('settings')"
                    >Settings</a
                >
            </div>
            <div class="col px-2">
                <a
                    href="#"
                    class="tab border-bottom text-center d-block w-100 pb-3 light-text"
                    :class="{ active: tab == 'members' }"
                    @click.prevent="openTab('members')"
                    >Members</a
                >
            </div>
            <div class="col px-2">
                <a
                    href="#"
                    class="tab border-bottom text-center d-block w-100 pb-3 light-text"
                    :class="{ active: tab == 'scraper' }"
                    @click.prevent="openTab('scraper')"
                    >Scraper</a
                >
            </div>
        </div>
        <div class="pt-3">
            <component
                v-bind:is="currentTabComponent"
                :team="team"
                :partner="partner"
                :teamMetrics="teamMetrics"
                :teamMembers="teamMembers"
                :teams="teams"
                @update-triggered="update"
            ></component>
        </div>
    </div>
    <div v-else>
        <div class="loader d-block text-center">
            <p><i class="fas fa-circle-notch fa-spin"></i></p>
            <p>Loading..</p>
        </div>
    </div>

    <!-- Modal for adding Watcher ID -->
    <div class="modal fade" id="addWatcherIdModal" tabindex="-1" role="dialog" aria-labelledby="addWatcherIdModalLabel" aria-hidden="true">
        <div class="modal-dialog" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title" id="addWatcherIdModalLabel">Add Watcher ID</h5>
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body">
                    <input type="text" v-model="newWatcherId" class="form-control" placeholder="Enter Watcher ID" />
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-dismiss="modal">Close</button>
                    <button type="button" class="btn btn-primary" @click="addWatcherId">Save</button>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import ManageSubscription from "./tabs/ManageSubscription";
import ManageSettings from "./tabs/ManageSettings";
import ManageMembers from "./tabs/ManageMembers";
import ManageScraper from "./tabs/ManageScraper";
import AdministrationMixin from "./AdministrationMixin";

export default {
    components: {
        ManageSubscription,
        ManageSettings,
        ManageMembers,
        ManageScraper
    },
    props: {
        team: Object,
        teams: Array,
        partner: Object
    },
    mixins: [AdministrationMixin],
    data() {
        return {
            newWatcherId: '',
            tab: "subscription",
            teamMetrics: null,
            busy: false
        };
    },
    computed: {
        currentTabComponent() {
            switch (this.tab) {
                case "subscription": {
                    return "manage-subscription";
                }
                case "settings": {
                    return "manage-settings";
                }
                case "members": {
                    return "manage-members";
                }
                case "scraper": {
                    return "manage-scraper";
                }
            }

            return "";
        },
        trialEndsAt() {
            return moment(this.team.trial_ends_at).format("DD MMM YYYY");
        }
    },
    watch: {
        team: {
            immediate: true,
            handler: function(newVal, oldVal) {
                this.getTeamMetrics();
                this.getTeamMembers();
            }
        }
    },
    methods: {
        showAddWatcherIdPopup() {
            $('#addWatcherIdModal').modal('show');
        },
        validateAndAddWatcherId() {
            if (!this.newWatcherId.match(/^\d+$/)) {
                this.watcherIdError = 'Watcher ID must be a number.';
                return;
            }
            this.watcherIdError = '';
            this.addWatcherId();
        },
        addWatcherId() {
            if (this.newWatcherId) {
                axios.put(`/api/admin/teams/${this.team.id}/update-watcher-id`, {
                    watcher_id: this.newWatcherId
                })
                .then(response => {
                    this.team.behemoth_watcher_id = this.newWatcherId;
                    $('#addWatcherIdModal').modal('hide');
                    this.newWatcherId = '';
                })
                .catch(error => {
                    console.error('There was an error updating the watcher ID:', error);
                });
            }
        },
        openTab(tab) {
            this.tab = tab;
        },
        update() {
            this.$emit("update-all", true);
            this.getTeamMetrics();
            this.getTeamMembers();
        },
        getTeamMembers() {
            this.teamMembers = null;
            axios.get(this.membersUrl).then(response => {
                this.teamMembers = response.data;
            });
        },
        getTeamMetrics() {
            this.teamMetrics = null;
            axios.get(this.metricsUrl).then(response => {
                this.teamMetrics = response.data;
            });
        },
        deactivate() {
            this.busy = true;
            axios.post(this.deactivateUrl).then(response => {
                this.busy = false;
                this.update();
            });
        },
        reactivate() {
            this.busy = true;
            axios.post(this.reactivateUrl).then(response => {
                this.busy = false;
                this.update();
            });
        },
        enableInitialLoading() {
            this.busy = true;
            axios.put(`/api/admin/teams/${this.team.id}/enable-initial-loading`).then(response => {
                this.busy = false;
                this.update();
            });
        }
    }
};
</script>

<style scoped>
.tab {
    border-bottom-width: 2px;
}
.tab.active {
    color: #00aef1;
    border-bottom: solid 2px #00aef1 !important;
}
</style>
